import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Building2, Download, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell 
} from '../../GlobalManagement/Pages/masterPageStyles';

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const formatOTDisplay = (otValue) => {
  const val = parseFloat(otValue);
  if (!val || val <= 0) return "0h 0m";
  
  const totalMinutes = Math.round(val * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  
  return `${h}h ${m}m`;
};

const EMP_COLORS = [
  "bg-white",
  "bg-slate-50/50",
  "bg-blue-50/30",
];

const getRowColor = (index) => EMP_COLORS[index % EMP_COLORS.length];

const getAttendanceTextColor = (val) => {
  switch (val) {
    case "P":
      return "text-green-600";
    case "P(L)":
      return "text-orange-500";
    case "A":
      return "text-red-600";
    case "SL":
    case "SL(OFF)":
      return "text-purple-600";
    case "CL":
    case "CL(OFF)":
      return "text-blue-600";
    case "OFF":
    case "OFF(EXCH)":
      return "text-slate-900";
    default:
      return "text-slate-700";
  }
};

const formatMonthForApi = (value) => {
  const [year, month] = String(value || "").split("-");
  if (!year || !month) return "";
  return new Date(Number(year), Number(month) - 1).toLocaleString("en-US", {
    month: "short",
  }) + `-${year}`;
};

const EmployeeAttendanceHistory = () => {
  const attendanceCacheRef = useRef({});
  const PER_PAGE_STORAGE_KEY = "employeeAttendanceHistory.pageSize";
  const getStoredPageSize = () => {
    const raw = localStorage.getItem(PER_PAGE_STORAGE_KEY);
    if (!raw) return 18;
    if (raw === "all" || raw === "-1") return -1;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : 18;
  };

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const [employees, setEmployees] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [shiftMap, setShiftMap] = useState({});
  const [designations, setDesignations] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState("ALL");
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  const [loading, setLoading] = useState(false);
  const tableContainerRef = useRef(null);

  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  /* ================= DAYS IN MONTH ================= */
  const daysInMonth = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const totalDays = new Date(year, month, 0).getDate();
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  }, [selectedMonth]);

  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthMeta = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    return { year, month };
  }, [selectedMonth]);

  const getDayLabel = (day) => {
    if (!monthMeta.year || !monthMeta.month) return "";
    const idx = new Date(monthMeta.year, monthMeta.month - 1, day).getDay();
    return dayLabels[idx] || "";
  };

  /* ================= FETCH EMPLOYEES ================= */
  useEffect(() => {
    axios.get("/api/employees").then((res) => {
      setEmployees((res.data || []).filter((emp) => !isExEmployeeId(emp.employeeID)));
    });
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      let filteredEmployees = employees;
      if (selectedDepartment !== "ALL") {
        filteredEmployees = employees.filter(
          (emp) => emp.departmentName === selectedDepartment
        );
      }
      const uniqueDesignations = [
        "ALL",
        ...new Set(filteredEmployees.map((emp) => emp.designationName)),
      ];
      setDesignations(uniqueDesignations);
    }
  }, [employees, selectedDepartment]);

  useEffect(() => {
    axios.get("/api/departments").then((res) => {
      setDepartments(["ALL", ...res.data.map((d) => d.deptName)]);
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, selectedDepartment, selectedDesignation, selectedEmployeePrefix, searchTerm, pageSize]);

  const employeePrefixOptions = useMemo(() => {
    const prefixes = employees
      .map((emp) => getEmployeePrefix(emp.employeeID))
      .filter(Boolean);
    return ["ALL", ...new Set(prefixes)];
  }, [employees]);

  useEffect(() => {
    localStorage.setItem(PER_PAGE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  /* ================= FILTER ================= */
  const filteredEmployees = useMemo(() => {
    const q = String(searchTerm || "").toUpperCase().trim();
    return employees.filter((emp) => {
      const departmentMatch =
        selectedDepartment === "ALL" ||
        emp.departmentName === selectedDepartment;

      const designationMatch =
        selectedDesignation === "ALL" ||
        emp.designationName === selectedDesignation;
      const prefixMatch =
        selectedEmployeePrefix === "ALL" ||
        getEmployeePrefix(emp.employeeID) === selectedEmployeePrefix;

      if (!departmentMatch || !designationMatch || !prefixMatch) return false;
      if (!q) return true;

      const name = `${emp.firstName || ""} ${emp.middleName || ""} ${emp.lastName || ""}`
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
      const employeeId = String(emp.employeeID || "").toUpperCase();
      const employeeUserId = String(emp.employeeUserId || "").toUpperCase();
      return name.includes(q) || employeeId.includes(q) || employeeUserId.includes(q);
    });
  }, [employees, selectedDepartment, selectedDesignation, selectedEmployeePrefix, searchTerm]);

  /* ================= EXPORT FUNCTION ================= */
  const exportToExcel = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    
    const headers = [
      "Emp User ID", "Emp ID", "Name", "Designation", "Department", 
      "Present", "Absent", "Offday", "SL", "CL", 
      ...daysInMonth.map(d => `Day ${d}`)
    ];
    csvContent += headers.join(",") + "\r\n";

    filteredEmployees.forEach((emp) => {
      const att = attendanceMap?.[emp.employeeUserId] || {};
      const name = `"${emp.firstName || ""} ${emp.middleName || ""} ${emp.lastName || ""}"`.trim();
      
      const rowData = [
        emp.employeeUserId || "",
        emp.employeeID || "",
        name,
        `"${emp.designationName || ""}"`,
        `"${emp.departmentName || ""}"`,
        att.totalPresent || 0,
        att.totalAbsent || 0,
        att.totalOff || 0,
        att.totalSL || 0,
        att.totalCL || 0,
        ...daysInMonth.map(day => att?.days?.[day]?.status || "")
      ];
      csvContent += rowData.join(",") + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `attendance_${selectedMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ================= FETCH ATTENDANCE ================= */
  useEffect(() => {
    const fetchAttendance = () => {
      const [yearStr, monthStr] = selectedMonth.split("-");
      const year = Number(yearStr);
      const month = Number(monthStr);

      setLoading(true);

      axios
        .get(`/api/attendance/history?month=${month}&year=${year}&skipSync=true&_t=${Date.now()}`)
        .then((res) => {
          const map = {};
          res.data.forEach((doc) => {
            const dayMap = {};
            doc.records.forEach((r) => {
              const day = new Date(r.date).getDate();
              let status = "";
              if (r.status === "Present" && r.isLate) status = "P(L)";
              else if (r.status === "Present") status = "P";
              else if (r.status === "Absent") status = "A";
              else status = r.status;

              dayMap[day] = {
                status,
                shiftCode: r.shiftCode || "-",
                shiftStartTime: r.shiftStartTime || "-",
                shiftEndTime: r.shiftEndTime || "-",
              };
            });

            map[doc.employeeUserId] = {
              days: dayMap,
              totalPresent: doc.totalPresent || 0,
              totalAbsent: doc.totalAbsent || 0,
              totalOff: doc.totalOff || 0,
              totalLeave: doc.totalLeave || 0,
              totalSL: doc.records ? doc.records.filter(r => String(r.status).split('(')[0] === "SL").length : 0,
              totalCL: doc.records ? doc.records.filter(r => String(r.status).split('(')[0] === "CL").length : 0,
              totalOTHours: doc.totalOTHours || 0,
            };
          });

          setAttendanceMap(map);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          toast.error("Failed to load attendance history");
        });
    };

    fetchAttendance();

    window.addEventListener("focus", fetchAttendance);
    return () => window.removeEventListener("focus", fetchAttendance);
  }, [selectedMonth]);

  useEffect(() => {
    const fetchShiftMap = async () => {
      try {
        const monthParam = formatMonthForApi(selectedMonth);
        if (!monthParam) {
          setShiftMap({});
          return;
        }

        const res = await axios.get(`/api/shift-management/${monthParam}`);
        const nextMap = {};
        (res.data || []).forEach((item) => {
          const userId = String(item?.employeeUserId || "").trim();
          if (!userId) return;
          nextMap[userId] = {};
          Object.entries(item?.shifts || {}).forEach(([day, code]) => {
            nextMap[userId][Number(day)] = String(code || "").trim().toUpperCase();
          });
        });
        setShiftMap(nextMap);
      } catch (err) {
        setShiftMap({});
      }
    };

    fetchShiftMap();
  }, [selectedMonth]);

  const startIndex = pageSize === -1 ? 0 : (currentPage - 1) * pageSize;
  const paginatedEmployees = pageSize === -1 
    ? filteredEmployees 
    : filteredEmployees.slice(startIndex, startIndex + pageSize);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 400;
      tableContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section matching DepartmentMaster */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Building2 size={14} /></div>
              <div><h1 className={masterTitle}>Attendance History</h1></div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exportToExcel}
                className={masterAccentButton}
              >
                <Download size={10} /> Export Excel
              </button>
              <BackButton />
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full flex-1 flex flex-col min-h-0`}>
          <MobileHeaderToggle>
            <div className={`${masterPanel} p-3 mb-3 border border-slate-200 bg-white`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full items-end">
                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">Month</label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="border border-slate-300 rounded px-2.5 py-1 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">Department</label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => {
                      setSelectedDepartment(e.target.value);
                      setSelectedDesignation("ALL");
                    }}
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {departments.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">Designation</label>
                  <select
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {designations.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">Status</label>
                  <select
                    value={selectedEmployeePrefix}
                    onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {employeePrefixOptions.map((prefix) => (
                      <option key={prefix}>{prefix}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-3 border-t border-slate-200 pt-2.5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] font-bold text-slate-700">
                  <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-green-600 rounded-sm"></span>Present (P)</div>
                  <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-orange-500 rounded-sm"></span>Late (P(L))</div>
                  <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-red-600 rounded-sm"></span>Absent (A)</div>
                  <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-purple-600 rounded-sm"></span>Sick (SL)</div>
                  <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-blue-600 rounded-sm"></span>Casual (CL)</div>
                  <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-slate-900 rounded-sm"></span>OFF</div>
                </div>

                <div className="flex flex-wrap gap-3 bg-slate-100 px-2.5 py-1.5 rounded text-[10px] font-bold text-slate-700">
                  <span className="text-green-700">TP - Total Present</span>
                  <span className="text-red-600">TA - Total Absent</span>
                  <span className="text-slate-600">TO - Total OFF</span>
                  <span className="text-orange-600">TL - Total Leave (SL+CL)</span>
                  <span className="text-orange-600">TOT - Total Over Time</span>
                </div>
              </div>
            </div>
          </MobileHeaderToggle>

          <div className={`${masterPanel} flex flex-col overflow-hidden w-full flex-1 relative`}> 
            
            {/* SearchBar with Integrated Pagination */}
            <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2 z-20">
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(normalizeSearchInput(val));
                  setCurrentPage(1);
                }} 
                placeholder="Search name / user ID / emp ID..." 
                totalItems={filteredEmployees.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

            {/* Horizontal Scroll Arrows matching Duty Roster */}
            <button 
              type="button"
              onClick={() => scrollTable('left')}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-50 bg-white/90 hover:bg-white text-green-600 shadow-md border border-slate-200 p-2 rounded-full transition-all active:scale-90 hidden md:flex items-center justify-center"
              title="Scroll Left"
            >
              <ChevronLeft size={20} />
            </button>

            <button 
              type="button"
              onClick={() => scrollTable('right')}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-50 bg-white/90 hover:bg-white text-green-600 shadow-md border border-slate-200 p-2 rounded-full transition-all active:scale-90 hidden md:flex items-center justify-center"
              title="Scroll Right"
            >
              <ChevronRight size={20} />
            </button>

            {/* Scrollable Container with Fixed/Sticky Table Header */}
            <div 
              ref={tableContainerRef} 
              className="w-full flex-1 min-h-0 overflow-auto bg-white rounded-lg scroll-smooth relative"
            >
              <table className={masterTable}>
                <thead>
                  <tr className={masterStickyHeadRow}>
                    <th className={masterTableData} style={{ width: '50px' }}>SL</th>
                    <th className={masterTableData}>Emp ID</th>
                    <th className={masterTableData}>User ID</th>
                    <th className={masterTableData}>Name</th>
                    <th className={masterTableData}>Department</th>
                    <th className={masterTableData}>Designation</th>
                    {daysInMonth.map((d) => (
                      <th
                        key={d}
                        className={masterTableData}
                        style={{ width: "32px", minWidth: "32px", maxWidth: "32px", textAlign: "center" }}
                      >
                        <div className="flex flex-col items-center leading-tight">
                          <span>{d}</span>
                          {/* High-visibility Day Labels (Mon, Tue, Wed, etc.) */}
                          <span className="text-[10px] font-bold text-slate-500 tracking-wide">
                            {getDayLabel(d)}
                          </span>
                        </div>
                      </th>
                    ))}
                    <th className={`${masterTableData} text-center bg-orange-600 text-white font-bold`}>TP</th>
                    <th className={`${masterTableData} text-center bg-orange-600 text-white font-bold`}>TA</th>
                    <th className={`${masterTableData} text-center bg-orange-600 text-white font-bold`}>TO</th>
                    <th className={`${masterTableData} text-center bg-orange-600 text-white font-bold`}>TL</th>
                    <th className={`${masterTableData} text-center bg-orange-600 text-white font-bold`}>TOT</th>
                  </tr>
                </thead>

                <tbody className={masterTbody}>
                  {loading ? (
                    <tr>
                      <td colSpan={daysInMonth.length + 11} className="text-center py-12 bg-white">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Loader2 className="animate-spin text-slate-600" size={24} />
                          <span className="text-xs font-semibold text-slate-600">
                            Loading Attendance Data... Please wait...
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={daysInMonth.length + 11} className="p-12 text-center text-slate-400 font-bold uppercase text-xs">
                        No employees found.
                      </td>
                    </tr>
                  ) : (
                    paginatedEmployees.map((emp, i) => (
                      <tr key={emp.employeeUserId} className={`${masterTableRow} ${getRowColor(startIndex + i)}`}>
                        <td className={masterTableCell}>{pageSize === -1 ? i + 1 : startIndex + i + 1}</td>
                        <td className={`${masterTableCell} uppercase`}>{emp.employeeID}</td>
                        <td className={`${masterTableCell} uppercase`}>{emp.employeeUserId}</td>
                        <td className={`${masterTableCell} font-medium uppercase`}>
                          {emp.firstName} {emp.middleName} {emp.lastName}
                        </td>
                        <td className={`${masterTableCell} uppercase`}>{emp.departmentName}</td>
                        <td className={`${masterTableCell} uppercase`}>{emp.designationName}</td>

                        {daysInMonth.map((day) => {
                          const valObj = attendanceMap?.[emp.employeeUserId]?.days?.[day];
                          const assignedShiftCode = shiftMap?.[emp.employeeUserId]?.[day] || "";
                          const currentStatus = valObj?.status || "";

                          const editableStatuses = ["P", "P(L)", "A"];
                          const isEditable = Boolean(valObj) && editableStatuses.includes(currentStatus);
                          const cellTitle = valObj
                            ? `Shift: ${valObj.shiftCode}\nStart: ${valObj.shiftStartTime}\nEnd: ${valObj.shiftEndTime}`
                            : assignedShiftCode
                            ? `Shift: ${assignedShiftCode}\nAttendance not marked`
                            : "";

                          return (
                            <td
                              key={day}
                              className={`border border-slate-300 text-center font-bold whitespace-nowrap overflow-hidden text-ellipsis ${getAttendanceTextColor(
                                currentStatus
                              )}`}
                              style={{
                                width: "32px",
                                minWidth: "32px",
                                maxWidth: "32px",
                                cursor: isEditable ? "pointer" : "not-allowed",
                              }}
                              title={cellTitle}
                            >
                              {isEditable ? (
                                <select
                                  value={currentStatus}
                                  onChange={async (e) => {
                                    const newStatus = e.target.value;

                                    toast(
                                      (t) => (
                                        <div className="flex flex-col gap-3 min-w-[220px]">
                                          <div className="flex items-center gap-3">
                                            <div className="w-1.5 h-10 bg-green-500 rounded-full"></div>
                                            <p className="text-xs font-semibold text-slate-800">
                                              Set attendance to{" "}
                                              <span className="text-green-600 font-bold underline">
                                                {newStatus}
                                              </span>
                                              ?
                                            </p>
                                          </div>

                                          <div className="flex justify-end items-center gap-2 border-t border-slate-100 pt-2">
                                            <button
                                              className="bg-slate-100 text-slate-600 hover:bg-slate-200 text-[10px] font-bold px-2.5 py-1 rounded transition-all uppercase"
                                              onClick={() => toast.dismiss(t.id)}
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              className="bg-green-600 hover:bg-green-700 text-white text-[10px] font-bold px-3 py-1 rounded shadow transition-all uppercase"
                                              onClick={async () => {
                                                toast.dismiss(t.id);

                                                const payload = {
                                                  employeeUserId: emp.employeeUserId,
                                                  date: `${selectedMonth}-${String(day).padStart(2, "0")}`,
                                                  status: newStatus === "P" || newStatus === "P(L)" ? "Present" : "Absent",
                                                  isLate: newStatus === "P(L)",
                                                };

                                                try {
                                                  await axios.put("/api/attendance/update", payload);
                                                  attendanceCacheRef.current = {};
                                                  window.location.reload(); 
                                                } catch (err) {
                                                  toast.error("Update failed!");
                                                }
                                              }}
                                            >
                                              Confirm
                                            </button>
                                          </div>
                                        </div>
                                      ),
                                      {
                                        duration: 6000,
                                        position: "top-center",
                                        style: {
                                          padding: "12px",
                                          borderRadius: "8px",
                                          border: "1px solid #e2e8f0",
                                          background: "#ffffff",
                                        },
                                      }
                                    );
                                  }}
                                  className={`bg-transparent text-center font-bold w-full cursor-pointer ${getAttendanceTextColor(
                                    currentStatus
                                  )}`}
                                >
                                  <option value="P">P</option>
                                  <option value="P(L)">P(L)</option>
                                  <option value="A">A</option>
                                </select>
                              ) : (
                                currentStatus
                              )}
                            </td>
                          );
                        })}

                        <td className={`${masterTableCell} text-center font-bold text-green-700 bg-green-50/20`}>
                          {attendanceMap?.[emp.employeeUserId]?.totalPresent || 0}
                        </td>
                        <td className={`${masterTableCell} text-center font-bold text-red-600 bg-red-50/20`}>
                          {attendanceMap?.[emp.employeeUserId]?.totalAbsent || 0}
                        </td>
                        <td className={`${masterTableCell} text-center font-bold text-slate-600 bg-slate-50/20`}>
                          {attendanceMap?.[emp.employeeUserId]?.totalOff || 0}
                        </td>
                        <td className={`${masterTableCell} text-center font-bold text-orange-600 bg-orange-50/20`}>
                          {attendanceMap?.[emp.employeeUserId]?.totalLeave || 0}
                        </td>
                        <td className={`${masterTableCell} text-center font-bold text-red-600 bg-amber-50/20`}>
                          {formatOTDisplay(attendanceMap?.[emp.employeeUserId]?.totalOTHours || 0)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EmployeeAttendanceHistory;